import {
  IconCalendar,
  IconDevices,
  IconMail,
  IconTrash,
  IconUser,
  IconUserCog,
} from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import type { AdminUser } from "@/lib/admin-types";
import {
  formatDate,
  formatRelativeTime,
  getUserRoleLabelKey,
  getUserRoles,
  getUserStatus,
} from "@/lib/admin-utils";
import { authClient } from "@/lib/auth-client";
import { queryKeys } from "@/lib/query-keys";

// Constants
const SESSION_ID_PREVIEW_LENGTH = 8;

type UserDetailsProps = {
  user: AdminUser;
  onEdit: () => void;
};

export function UserDetails({ user, onEdit }: UserDetailsProps) {
  const { t } = useTranslation();
  const status = getUserStatus(user);
  const roles = getUserRoles(user);

  const formatUserRole = (role: string) => {
    const labelKey = getUserRoleLabelKey(role);
    return labelKey ? t(labelKey) : role;
  };

  // Fetch user sessions
  const { data: sessions, refetch: refetchSessions } = useQuery({
    queryKey: queryKeys.admin.userSessions(user.id),
    queryFn: async () => {
      const response = await authClient.admin.listUserSessions({
        userId: user.id,
      });
      if (!response.data) {
        throw new Error("Failed to fetch sessions");
      }
      return response.data;
    },
  });

  // Revoke session mutation
  const revokeSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await authClient.admin.revokeUserSession({
        sessionToken: sessionId,
      });
      if (!response.data) {
        throw new Error("Failed to revoke session");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.user.sessions.sessionRevoked"));
      refetchSessions();
    },
    onError: (error) => {
      toast.error(t("admin.user.sessions.revokeSessionFailed", { error: error.message }));
    },
  });

  // Revoke all sessions mutation
  const revokeAllSessionsMutation = useMutation({
    mutationFn: async () => {
      const response = await authClient.admin.revokeUserSessions({
        userId: user.id,
      });
      if (!response.data) {
        throw new Error("Failed to revoke all sessions");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.user.sessions.allSessionsRevoked"));
      refetchSessions();
    },
    onError: (error) => {
      toast.error(t("admin.user.sessions.revokeSessionFailed", { error: error.message }));
    },
  });

  return (
    <div className="space-y-6">
      {/* User Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <IconUser className="h-5 w-5" />
                {t("admin.user.information.title")}
              </CardTitle>
              <CardDescription>{t("admin.user.information.description")}</CardDescription>
            </div>
            <Button onClick={onEdit} variant="outline">
              <IconUserCog className="mr-2 h-4 w-4" />
              {t("admin.user.information.edit")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <div className="font-medium text-muted-foreground text-sm">
                {t("admin.user.information.email")}
              </div>
              <div className="flex items-center gap-2">
                <IconMail className="h-4 w-4" />
                {user.email}
              </div>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-muted-foreground text-sm">
                {t("admin.user.information.name")}
              </div>
              <div>{user.name || t("admin.user.information.notProvided")}</div>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-muted-foreground text-sm">
                {t("admin.user.information.status")}
              </div>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-muted-foreground text-sm">
                {t("admin.user.information.roles")}
              </div>
              <div className="flex gap-1">
                {roles.map((role) => (
                  <Badge key={role} variant="outline">
                    {formatUserRole(role)}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-muted-foreground text-sm">
                {t("admin.user.information.emailVerified")}
              </div>
              <Badge variant={user.emailVerified ? "default" : "secondary"}>
                {user.emailVerified
                  ? t("admin.user.information.verified")
                  : t("admin.user.information.notVerified")}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-muted-foreground text-sm">
                {t("admin.user.information.accountCreated")}
              </div>
              <div className="flex items-center gap-2">
                <IconCalendar className="h-4 w-4" />
                <div>
                  <div>{formatDate(user.createdAt)}</div>
                  <div className="text-muted-foreground text-xs">
                    {formatRelativeTime(user.createdAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {user.banned && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="font-medium text-muted-foreground text-sm">
                  {t("admin.user.information.banInfo")}
                </div>
                <div className="space-y-1">
                  {user.banReason && (
                    <div className="text-sm">
                      <span className="font-medium">{t("admin.user.information.reason")}:</span>{" "}
                      {user.banReason}
                    </div>
                  )}
                  {user.banExpires && (
                    <div className="text-sm">
                      <span className="font-medium">{t("admin.user.information.expires")}:</span>{" "}
                      {formatDate(user.banExpires)}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <IconDevices className="h-5 w-5" />
                {t("admin.user.sessions.title")}
              </CardTitle>
              <CardDescription>{t("admin.user.sessions.description")}</CardDescription>
            </div>
            <Button
              disabled={!sessions?.sessions?.length || revokeAllSessionsMutation.isPending}
              onClick={() => revokeAllSessionsMutation.mutate()}
              size="sm"
              variant="destructive"
            >
              {t("admin.user.sessions.revokeAll")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sessions?.sessions?.length ? (
            <div className="space-y-3">
              {sessions.sessions.map(
                (session: {
                  id: string;
                  token: string;
                  createdAt: Date;
                  expiresAt: Date;
                  ipAddress?: string | null;
                  userAgent?: string | null;
                  impersonatedBy?: string | null;
                }) => (
                  <div
                    className="flex items-center justify-between rounded-lg border p-3"
                    key={session.id}
                  >
                    <div className="space-y-1">
                      <div className="font-medium text-sm">
                        {t("admin.user.sessions.session")}{" "}
                        {session.id.slice(0, SESSION_ID_PREVIEW_LENGTH)}
                        ...
                      </div>
                      <div className="space-y-1 text-muted-foreground text-xs">
                        <div>
                          {t("admin.user.sessions.created")}: {formatDate(session.createdAt)}
                        </div>
                        <div>
                          {t("admin.user.sessions.expires")}: {formatDate(session.expiresAt)}
                        </div>
                        {session.ipAddress && (
                          <div>
                            {t("admin.user.sessions.ip")}: {session.ipAddress}
                          </div>
                        )}
                        {session.userAgent && (
                          <div>
                            {t("admin.user.sessions.userAgent")}: {session.userAgent}
                          </div>
                        )}
                        {session.impersonatedBy && (
                          <div className="text-orange-600">
                            {t("admin.user.sessions.impersonatedBy")}: {session.impersonatedBy}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      disabled={revokeSessionMutation.isPending}
                      onClick={() => revokeSessionMutation.mutate(session.id)}
                      size="sm"
                      variant="destructive"
                    >
                      <IconTrash className="h-4 w-4" />
                    </Button>
                  </div>
                ),
              )}
            </div>
          ) : (
            <div className="py-6 text-center text-muted-foreground">
              {t("admin.user.sessions.noSessions")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
